import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { FC, ReactNode } from 'react';

// 確認メッセージの可変部分（台数/日数/件数）は Lovable に倣い太字で強調する。
const b = (value: ReactNode): ReactNode => <b>{value}</b>;

const COPY = {
  apply: {
    title: '既存の計画を上書きします',
    whole: '上書きする',
    split: '選択日のみ',
    wholeAll: '連続勤務 全期間',
    lead: (n: number): ReactNode => <>選択範囲に既存の勤務が {b(`${n}件`)} あります。上書きしてよろしいですか？</>,
    blocked: (n: number): ReactNode => <>確定済/固定済、または他トラックと共有する勤務が {b(`${n}件`)} あるため上書きできません。</>,
  },
  remove: {
    title: '選択シフトの一括解除',
    whole: '解除する',
    split: '選択日のみ解除',
    wholeAll: '連続勤務 全期間 解除',
    lead: (n: number, truckCount: number, dateCount: number): ReactNode => (
      <>選択中の {b(`${truckCount}台`)} × {b(`${dateCount}日`)} に登録されている {b(`${n}件`)} のシフトを解除します。</>
    ),
    blocked: (n: number): ReactNode => <>確定済/固定済、または他トラックと共有する勤務が {b(`${n}件`)} あるため解除できません。</>,
  },
};

type Props = {
  open: boolean;
  action?: 'apply' | 'remove';
  shiftCount: number;
  partialMultiDayCount?: number;
  splitAvailable: boolean;
  wholeAvailable: boolean;
  blockedCount?: number;
  truckCount?: number;
  dateCount?: number;
  applying: boolean;
  onSplit: () => void;
  onWhole: () => void;
  onCancel: () => void;
};

export const OverwriteConfirmDialog: FC<Props> = ({
  open, action = 'apply', shiftCount, partialMultiDayCount = 0, splitAvailable, wholeAvailable, blockedCount = 0,
  truckCount = 0, dateCount = 0,
  applying, onSplit, onWhole, onCancel,
}) => {
  const copy = COPY[action];
  // 解除(remove)は破壊操作なので主ボタンを赤(error)、反映(apply)は primary（Lovable 準拠）。
  const primaryColor: 'primary' | 'error' = action === 'remove' ? 'error' : 'primary';
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 16 }}>{copy.title}</DialogTitle>
      <DialogContent>
        {wholeAvailable ? (
          <DialogContentText sx={{ fontSize: 13 }}>
            {copy.lead(shiftCount, truckCount, dateCount)}
            {splitAvailable && `（連続勤務 ${partialMultiDayCount} 件は選択日のみ切り出し）`}
          </DialogContentText>
        ) : (
          <DialogContentText sx={{ fontSize: 13 }}>
            {copy.blocked(blockedCount)}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={applying} sx={{ wordBreak: 'keep-all' }}>キャンセル</Button>
        {wholeAvailable && splitAvailable && (
          <>
            <Button onClick={onSplit} variant="outlined" color={primaryColor} disabled={applying}>{copy.split}</Button>
            <Button onClick={onWhole} variant="contained" color={primaryColor} disabled={applying}>{copy.wholeAll}</Button>
          </>
        )}
        {wholeAvailable && !splitAvailable && (
          <Button onClick={onWhole} variant="contained" color={primaryColor} disabled={applying}>{copy.whole}</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
